/**
 * Login Command
 * Authenticate with Paean AI using QR code or browser OAuth
 */

import { Command } from 'commander';
import { readFileSync } from 'fs';
import { qrLogin, browserLogin, emailLogin, walletLogin, getCurrentUser, validateToken } from '../api/auth.js';
import { registerDevice } from '../api/devices.js';
import { isAuthenticated, getConfigValue } from '../utils/config.js';
import * as output from '../utils/output.js';

export const loginCommand = new Command('login')
  .description('Authenticate with Paean AI')
  .option('--qr', 'Use QR code login (scan with mobile app)')
  .option('--browser', 'Use browser-based login')
  .option('--email', 'Use email/password login')
  .option('--wallet', 'Use wallet private key login (EVM or Solana)')
  .option('--chain <chain>', 'Wallet chain: evm or solana (default: evm)', 'evm')
  .option('--private-key <key>', 'Wallet private key (hex for EVM, base64 for Solana)')
  .option('--key-file <path>', 'Path to file containing wallet private key')
  .option('--check', 'Check current authentication status')
  .action(async (options) => {
    // Check current status
    if (options.check) {
      await checkAuthStatus();
      return;
    }

    // If already logged in, show status
    if (isAuthenticated()) {
      const email = getConfigValue('email');
      output.success(`Already logged in${email ? ` as ${email}` : ''}`);
      output.dim('Use "paean logout" to sign out, or "paean login --check" to verify.');
      return;
    }

    // Handle wallet login
    if (options.wallet) {
      await handleWalletLogin(options.chain, options.privateKey, options.keyFile);
      return;
    }

    // Handle email login
    if (options.email) {
      await handleEmailLogin();
      return;
    }

    // Determine login method
    const useQr = options.qr;
    const useBrowser = options.browser;

    // Default to browser if neither specified
    const method = useQr ? 'qr' : useBrowser ? 'browser' : 'browser';

    output.header('Paean AI Login');

    const spin = output.spinner(
      method === 'qr' ? 'Creating QR session...' : 'Preparing login...'
    ).start();

    try {
      let result: { success: boolean; error?: string };

      if (method === 'qr') {
        spin.stop();
        result = await qrLogin((status) => {
          if (status === 'scanned') {
            output.info('QR code scanned! Confirming...');
          }
        });
      } else {
        spin.stop();
        result = await browserLogin();
      }

      if (result.success) {
        output.newline();
        output.success('Login successful!');

        // Register device session
        try {
          await registerDevice();
        } catch {
          // Best effort device registration
        }

        // Show user info
        const user = await getCurrentUser();
        if (user) {
          output.tableRow('Email', user.email);
          if (user.name) {
            output.tableRow('Name', user.name);
          }
        }

        output.newline();
        output.dim('You can now use "paean tasks" to view your tasks.');
        process.exit(0);
      } else {
        output.newline();
        output.error(result.error || 'Login failed');
        process.exit(1);
      }
    } catch (error) {
      spin.stop();
      const message = error instanceof Error ? error.message : 'Unknown error';
      output.error(`Login failed: ${message}`);
      output.dim('Please check your internet connection and try again.');
      process.exit(1);
    }
  });

async function handleWalletLogin(
  chain: string,
  privateKeyArg?: string,
  keyFilePath?: string,
): Promise<void> {
  const validChain = chain === 'solana' ? 'solana' : 'evm';

  // Resolve private key from arg, file, or env
  let privateKey = privateKeyArg;

  if (!privateKey && keyFilePath) {
    try {
      privateKey = readFileSync(keyFilePath, 'utf-8').trim();
    } catch (err) {
      output.error(`Failed to read key file: ${keyFilePath}`);
      process.exit(1);
    }
  }

  if (!privateKey) {
    privateKey = process.env.PAEAN_WALLET_KEY;
  }

  if (!privateKey) {
    output.error('No private key provided.');
    output.dim('Provide via --private-key <hex>, --key-file <path>, or env PAEAN_WALLET_KEY');
    process.exit(1);
  }

  output.header(`Paean AI Login (Wallet - ${validChain.toUpperCase()})`);
  const spin = output.spinner('Authenticating with wallet...').start();

  try {
    const result = await walletLogin(validChain, privateKey);
    spin.stop();

    if (result.success) {
      output.newline();
      output.success('Wallet login successful!');
      if (result.address) {
        output.tableRow('Address', result.address);
      }
      output.tableRow('Chain', validChain.toUpperCase());

      try {
        await registerDevice();
      } catch {
        // Best effort
      }

      const user = await getCurrentUser();
      if (user) {
        output.tableRow('Email', user.email);
        if (user.name) {
          output.tableRow('Name', user.name);
        }
      }

      output.newline();
      output.dim('You can now use "paean tasks" to view your tasks.');
      process.exit(0);
    } else {
      output.newline();
      output.error(result.error || 'Wallet login failed');
      process.exit(1);
    }
  } catch (error) {
    spin.stop();
    const message = error instanceof Error ? error.message : 'Unknown error';
    output.error(`Wallet login failed: ${message}`);
    process.exit(1);
  }
}

async function handleEmailLogin(): Promise<void> {
  const readline = await import('readline');

  output.header('Paean AI Login (Email)');
  output.newline();

  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  });

  const email = await new Promise<string>((resolve) => {
    rl.question('  Email: ', resolve);
  });

  if (!email.trim()) {
    rl.close();
    output.error('Email is required.');
    process.exit(1);
  }

  // Read password without echoing (manual raw mode)
  const password = await new Promise<string>((resolve) => {
    process.stdout.write('  Password: ');

    if (process.stdin.isTTY) {
      let pw = '';
      process.stdin.setRawMode(true);
      process.stdin.resume();
      process.stdin.setEncoding('utf-8');

      const onData = (ch: string) => {
        if (ch === '\n' || ch === '\r' || ch === '\u0004') {
          process.stdin.setRawMode(false);
          process.stdin.pause();
          process.stdin.removeListener('data', onData);
          process.stdout.write('\n');
          resolve(pw);
        } else if (ch === '\u0003') {
          process.exit(0);
        } else if (ch === '\u007f' || ch === '\b') {
          if (pw.length > 0) {
            pw = pw.slice(0, -1);
            process.stdout.write('\b \b');
          }
        } else if (ch.charCodeAt(0) >= 32) {
          pw += ch;
          process.stdout.write('*');
        }
      };

      process.stdin.on('data', onData);
    } else {
      rl.question('', resolve);
    }
  });

  rl.close();

  if (!password.trim()) {
    output.error('Password is required.');
    process.exit(1);
  }

  const spin = output.spinner('Logging in...').start();

  try {
    const result = await emailLogin(email.trim(), password);
    spin.stop();

    if (result.success) {
      output.newline();
      output.success('Login successful!');

      // Register device
      try {
        await registerDevice();
      } catch {
        // Best effort
      }

      const user = await getCurrentUser();
      if (user) {
        output.tableRow('Email', user.email);
        if (user.name) {
          output.tableRow('Name', user.name);
        }
      }

      output.newline();
      output.dim('You can now use "paean tasks" to view your tasks.');
      process.exit(0);
    } else {
      output.newline();
      output.error(result.error || 'Login failed');
      process.exit(1);
    }
  } catch (error) {
    spin.stop();
    const message = error instanceof Error ? error.message : 'Unknown error';
    output.error(`Login failed: ${message}`);
    process.exit(1);
  }
}

async function checkAuthStatus(): Promise<void> {
  output.header('Authentication Status');

  if (!isAuthenticated()) {
    output.warning('Not logged in');
    output.dim('Use "paean login" to authenticate.');
    return;
  }

  const spin = output.spinner('Validating token...').start();

  try {
    const isValid = await validateToken();
    spin.stop();

    if (isValid) {
      const user = await getCurrentUser();
      output.success('Authenticated');

      if (user) {
        output.tableRow('Email', user.email);
        if (user.name) {
          output.tableRow('Name', user.name);
        }
        output.tableRow('User ID', String(user.id));
      }
    } else {
      output.warning('Token expired or invalid');
      output.dim('Use "paean login" to re-authenticate.');
    }
  } catch (error) {
    spin.stop();
    output.warning('Could not validate token');
    output.dim('Use "paean login" to re-authenticate.');
  }
}
